import { useEffect, useState, useRef, useCallback } from 'react';
import { AssistantRuntimeProvider } from '@assistant-ui/react';
import { useIvyChatAuth } from '@/hooks/useIvyChatAuth';
import { useWorkspaceIvySession } from '@/hooks/useWorkspaceIvySession';
import { useEmbeddedIvyRuntime } from '@/lib/ivyChat/useEmbeddedIvyRuntime';
import { useEmbeddedIvyChatStore, LEARN_TRIGGER_EVENT } from '@/stores/embeddedIvyChatStore';
import { useQualityTier } from '@/hooks/useQualityTier';
import { EmbeddedIvyThread } from './EmbeddedIvyThread';
import { StoryboardFeedbackProvider } from './StoryboardFeedbackB';
import { TakeoverOpening, TakeoverOrChat, type Takeover } from './IvyTakeoverB';
import { ReopenSummaryB } from './ReopenSummaryB';
import { studioBOn } from '@/lib/studioB/flag';
import { IconTipB } from '@/components/studio/IconTipB';
import { VideoCreditsUsed } from '@/components/video-workspace/VideoCreditsUsed';
import { EmbeddedIvyComposer } from './EmbeddedIvyComposer';
import { useSceneScope } from '@/lib/ivyChat/useSceneScope';
import { ClaudeTokenModal } from './ClaudeTokenModal';
import { LowCreditsBanner } from '@/components/billing/LowCreditsBanner';
import { BuildEstimate } from '@/components/billing/BuildEstimate';
import type { VideoSku } from '@/components/billing/videoEstimate';
import { Wand2, Settings } from 'lucide-react';
import { useStudio, useNow } from '@/components/studio/studioHooks';
import { StudioSummaryProvider } from '@/components/studio/StudioSummary';
import { StudioProgress } from '@/components/studio/StudioProgress';
import * as ivyChatApi from '@/services/ivyChatApi';
import { useStoryboardAutoSend } from '@/lib/ivyChat/useStoryboardAutoSend';
import { publishIvyState } from '@/lib/ivyChat/ivyNow';
import { storyboardIvyState } from '@/components/video-workspace/storyboard/feedbackWords';

/** Wait after a publish/export/turn so rapid republishes coalesce into one learn check. */
const LEARN_CHECK_DELAY_MS = 20_000;

/**
 * Checks for a pending learn whenever the chat becomes idle: after a normal turn ends and after
 * the UI's own publish/export (both debounced). Returns the scheduler for the mount check.
 */
function useSessionLearnCheck(conversationId: string) {
  const checkLearnPending = useEmbeddedIvyChatStore(s => s.checkLearnPending);
  const activeTurnKind = useEmbeddedIvyChatStore(s => s.activeTurnKind);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const prevTurnKindRef = useRef(activeTurnKind);

  const scheduleLearnCheck = useCallback((delayMs: number) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { void checkLearnPending(conversationId); }, delayMs);
  }, [conversationId, checkLearnPending]);

  useEffect(() => {
    if (prevTurnKindRef.current === 'normal' && activeTurnKind === null) scheduleLearnCheck(LEARN_CHECK_DELAY_MS);
    prevTurnKindRef.current = activeTurnKind;
  }, [activeTurnKind, scheduleLearnCheck]);

  useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent).detail?.conversationId === conversationId) scheduleLearnCheck(LEARN_CHECK_DELAY_MS);
    };
    window.addEventListener(LEARN_TRIGGER_EVENT, handler);
    return () => {
      window.removeEventListener(LEARN_TRIGGER_EVENT, handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [conversationId, scheduleLearnCheck]);

  return scheduleLearnCheck;
}

interface EmbeddedIvyChatProps {
  conversationId: string;
  conversationTitle?: string;
  systemContext?: string;
  autoSendMessage?: string;
  /** Set at the review stop, before the build: shows what the video will likely use. */
  estimateSku?: VideoSku;
  /** Direction B (beta only): before the outline, show /start's view while Ivy works, instead of the thread. */
  takeover?: Takeover;
  /** Direction B: the chat has moved from the centre to the rail. */
  docked?: boolean;
  /** Direction B on a phone: the sheet only peeks, so the room goes to Ivy's words and the composer. */
  peek?: boolean;
}

export function EmbeddedIvyChat({ conversationId, conversationTitle, systemContext, autoSendMessage, estimateSku, takeover, docked, peek }: EmbeddedIvyChatProps) {
  const { authReady, authError } = useIvyChatAuth();
  const [qualityTier] = useQualityTier();
  const { chatId, isLoading: sessionLoading, error: sessionError } = useWorkspaceIvySession(
    conversationId,
    conversationTitle,
    authReady,
    qualityTier,
  );

  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<{ hasToken: boolean; hint: string | null } | null>(null);
  const [platformFunded, setPlatformFunded] = useState(false);
  const [tokenChecked, setTokenChecked] = useState(false);
  const loadMessages = useEmbeddedIvyChatStore(s => s.loadMessages);
  const setPendingVoice = useEmbeddedIvyChatStore(s => s.setPendingVoice);
  const setChatId = useEmbeddedIvyChatStore(s => s.setChatId);
  const checkAndReconnect = useEmbeddedIvyChatStore(s => s.checkAndReconnect);
  const scheduleLearnCheck = useSessionLearnCheck(conversationId);
  const prevChatIdRef = useRef<string | null>(null);

  const checkTokenStatus = useCallback(async () => {
    setPlatformFunded(await ivyChatApi.isPlatformFunded());
    const providerStatus = await ivyChatApi.getProviderStatus();
    const activeP = providerStatus.providers[providerStatus.activeProvider];
    setTokenStatus({
      hasToken: providerStatus.hasAnyKey,
      hint: activeP?.hint || null,
    });
    setTokenChecked(true);
  }, []);

  useEffect(() => {
    if (authReady) checkTokenStatus();
  }, [authReady, checkTokenStatus]);

  useEffect(() => {
    if (!chatId || chatId === prevChatIdRef.current) return;
    prevChatIdRef.current = chatId;
    setChatId(chatId);
    // loadMessages flips the store's `messagesLoaded` flag when done; the inner
    // component's auto-send waits on it so it can't clobber the sent message.
    // Reconnecting and the learn check wait for the history: a turn that starts and ends on an empty thread makes
    // assistant-ui add and drop a placeholder message under the mounted thread (#1095), and a reconnect resumes
    // from the history's event count.
    loadMessages(chatId)
      .then(() => checkAndReconnect(chatId))
      .then(() => scheduleLearnCheck(0));
  }, [chatId, setChatId, loadMessages, checkAndReconnect, scheduleLearnCheck]);

  // Listen for voice selection events from InlineVoiceSelector
  // Instead of sending immediately, add the voice as a pending attachment in the composer
  useEffect(() => {
    const handler = (e: Event) => {
      const voice = (e as CustomEvent).detail?.voice;
      if (voice && chatId) {
        setPendingVoice(voice);
      }
    };
    window.addEventListener('ivy-voice-selected', handler);
    return () => window.removeEventListener('ivy-voice-selected', handler);
  }, [chatId, setPendingVoice]);

  // NOTE: the hero auto-send is handled inside EmbeddedIvyChatInner so it can go
  // through the assistant-ui runtime (the same path a composed message takes),
  // which is what makes the user's message render. See its autosend effect.

  if (authError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <p className="text-sm text-muted-foreground">{authError}</p>
      </div>
    );
  }

  const opening = takeover?.sent && !docked ? <TakeoverOpening takeover={takeover} /> : null;

  if (!authReady || sessionLoading || !tokenChecked) {
    if (opening) return opening;
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-6 w-6 border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <p className="text-sm text-destructive">{sessionError}</p>
      </div>
    );
  }

  if (!tokenStatus?.hasToken && !platformFunded) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full p-6 text-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand/10 flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-brand" />
          </div>
          <p className="text-sm font-medium text-foreground">Add your model key</p>
          <p className="text-xs text-muted-foreground max-w-[240px]">
            This chat runs on your own model key. It is encrypted and stored securely.
          </p>
          <button
            onClick={() => setShowTokenModal(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            <Settings className="w-3 h-3" />
            Configure
          </button>
        </div>
        <ClaudeTokenModal
          open={showTokenModal}
          onClose={() => {
            setShowTokenModal(false);
            checkTokenStatus();
          }}
        />
      </>
    );
  }

  if (!chatId) return opening;

  return (
    <>
      <EmbeddedIvyChatInner
        conversationId={conversationId}
        sessionChatId={chatId}
        systemContext={systemContext}
        autoSendMessage={autoSendMessage}
        tokenHint={tokenStatus?.hint ?? null}
        onOpenSettings={() => setShowTokenModal(true)}
        estimateSku={estimateSku}
        qualityTier={qualityTier}
        takeover={takeover}
        docked={docked}
        peek={peek}
      />
      <ClaudeTokenModal
        open={showTokenModal}
        onClose={() => {
          setShowTokenModal(false);
          checkTokenStatus();
        }}
      />
    </>
  );
}

function EmbeddedIvyChatInner({
  conversationId,
  sessionChatId,
  systemContext,
  autoSendMessage,
  tokenHint,
  onOpenSettings,
  estimateSku,
  qualityTier,
  takeover,
  docked,
  peek,
}: {
  conversationId: string;
  sessionChatId: string;
  systemContext?: string;
  autoSendMessage?: string;
  tokenHint: string | null;
  onOpenSettings: () => void;
  estimateSku?: VideoSku;
  qualityTier?: string;
  takeover?: Takeover;
  docked?: boolean;
  peek?: boolean;
}) {
  const { context, scene } = useSceneScope(systemContext);
  const runtime = useEmbeddedIvyRuntime(context, conversationId);

  // F4 (Direction B): a storyboard send goes to Ivy by itself, and the storyboard page hears what she is doing.
  useStoryboardAutoSend(conversationId);
  const ivyNow = useEmbeddedIvyChatStore((s) => storyboardIvyState(s.messages, s.isStreaming));
  useEffect(() => {
    if (studioBOn()) publishIvyState(conversationId, ivyNow);
  }, [conversationId, ivyNow]);

  // Auto-send the hero's first message by driving the assistant-ui COMPOSER (setText + send) —
  // the exact path a human send takes. The composer adds an optimistic user message to the
  // thread, which is why human sends render the bubble; runtime.thread.append and direct
  // store.sendMessage do NOT (the message lands in the external store at the same tick
  // isStreaming flips true, so the runtime treats it as the in-flight run input and never
  // commits the bubble until a reload re-syncs from the DB). send() still routes through onNew,
  // which applies the [view: story_development] context that auto-invokes /create-video-audio-first.
  // Gates: store synced to THIS session (guards a stale store from a prior project), history
  // loaded (can't clobber the send), no stream live (so the composer is enabled).
  const storeChatId = useEmbeddedIvyChatStore(s => s.chatId);
  const isStreaming = useEmbeddedIvyChatStore(s => s.isStreaming);
  const messagesLoaded = useEmbeddedIvyChatStore(s => s.messagesLoaded);
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (!autoSendMessage?.trim() || autoSentRef.current) return;
    const key = `ivyAutoSend-${conversationId}`;
    if (sessionStorage.getItem(key)) return;
    if (storeChatId !== sessionChatId || !messagesLoaded || isStreaming) return;
    autoSentRef.current = true;
    sessionStorage.setItem(key, 'true');
    runtime.thread.composer.setText(autoSendMessage.trim());
    runtime.thread.composer.send();
  }, [autoSendMessage, conversationId, sessionChatId, storeChatId, messagesLoaded, isStreaming, runtime]);

  const studio = useStudio();
  const now = useNow(!!studio?.working);
  const showProgress = !studioBOn() && !!studio && !studio.waiting && (studio.working || studio.stage === 'build');
  // A video opened in the rail (it did not start centre stage this visit) opens on the summary, its history folded.
  const reopened = studioBOn() && !!docked && !takeover;
  const openStoryboard = useCallback((version: number) => {
    window.dispatchEvent(new CustomEvent('storyboard-open', { detail: { conversationId, version } }));
  }, [conversationId]);

  const chatLayout = (
      <div className="flex flex-col h-full min-h-0 overflow-hidden">
        <StudioSummaryProvider value={studio ? { scenes: studio.scenes.length } : null}>
          {studioBOn() ? (
            <StoryboardFeedbackProvider conversationId={conversationId} onOpen={openStoryboard}>
              <EmbeddedIvyThread collapse={reopened} summary={reopened ? <ReopenSummaryB conversationId={conversationId} /> : undefined} />
            </StoryboardFeedbackProvider>
          ) : (
            <EmbeddedIvyThread />
          )}
        </StudioSummaryProvider>
        <div className="flex-shrink-0 space-y-2 px-3 pt-2 empty:hidden">
          {showProgress && <StudioProgress state={studio} now={now} className="max-h-[40vh] overflow-y-auto" />}
          <LowCreditsBanner refreshKey={isStreaming ? 'turn' : 'idle'} />
          {estimateSku && <BuildEstimate sku={estimateSku} tier={qualityTier} />}
        </div>
        <EmbeddedIvyComposer conversationId={conversationId} scene={scene} />
        {/* Always-visible settings button (not in a phone's peeking sheet) */}
        {!peek && <div className="flex-shrink-0 flex items-center justify-between px-3 py-1 border-t border-border-secondary">
          <span className="flex min-w-0 items-center gap-2">
            {studioBOn() && <VideoCreditsUsed conversationId={conversationId} variant="b-foot" />}
            <span className="text-[10px] text-muted-foreground/50 truncate">
              {tokenHint ? 'Your own key' : ''}
            </span>
          </span>
          <IconTipB label="Model key settings" side="top">
            <button
              onClick={onOpenSettings}
              className="p-1 rounded text-muted-foreground/40 hover:text-foreground hover:bg-muted transition-colors"
              title="Model key settings"
            >
              <Settings className="w-3 h-3" />
            </button>
          </IconTipB>
        </div>}
      </div>
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      {takeover ? (
        <TakeoverOrChat takeover={takeover} waiting={!!studio?.waiting} chat={chatLayout} composer={<EmbeddedIvyComposer conversationId={conversationId} scene={scene} />} docked={docked} />
      ) : (
        chatLayout
      )}
    </AssistantRuntimeProvider>
  );
}
